import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { useRouteMatch } from 'react-router-dom';

import { CallHistoryMethodAction, push } from 'connected-react-router';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

// Utils
import { isUpc } from '@webapp/utils/validators';
import { datesSection } from '@webapp/utils/validators/release';
import scrollOnError from '@webapp/utils/release/scroll-on-error';
import { validateRelease } from '@webapp/utils/validators/release';
// Services
import { Price } from '@common/types/price/price';
import { Product } from '@services/product/types/product';

// Store
import AppState from '@webapp/store/state';
import { plans } from '@webapp/store/products/selectors';
import { trackList } from '@webapp/store/tracks/selectors';
import { CreateOrder } from '@webapp/store/orders/action-creators';
import { UpdateReleaseStoreError } from '@webapp/store/release/action-creators';

import Box from '@components/layout/box';
import HintArrow from '@components/basics/hint-arrow';
import BoxHeader from '@components/layout/box-header';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import SummaryButton from '@components/actions/summary-button';
import ErrorMessage from '@webapp/components/basics/error-message/index';

// Controllers
import UPCCodeCtrl from '@webapp/controllers/release/upc-code';
// import AppleMusicCtrl from '@webapp/controllers/release/apple-music';
import PreorderReleaseCtrl from '@webapp/controllers/release/preorder';
import StoreSelectorCtrl from '@webapp/controllers/release/store-selector';
import RegionSelectorCtrl from '@webapp/controllers/release/region-selector';
import ReleaseHistoryCtrl from '@webapp/controllers/release/release-history';
import StoreMaximizerCtrl from '@webapp/controllers/release/store-maximizer';
import RemoveBrandingCtrl from '@webapp/controllers/release/remove-branding';
import YoutubeContentIdCtrl from '@webapp/controllers/release/youtube-content-id';
import CounterFixedButton from '@components/actions/counter-fixed-button';

interface ReleaseInfoDatePageProps {
  className?: string;
  stepValid: boolean;
  nextTrackId?: string;
  missingPreviousDate: boolean;
  missingReleaseDate: boolean;
  validUpc: boolean;
  hasStores: boolean;
  hasSelectedRegions: boolean;
  missingPreOrderDate: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isReleaseValid: boolean;
  nextSection: (trackId: string) => CallHistoryMethodAction;
  sendAnalytics: (
    field: string,
    value: string,
    error: string,
    releaseId?: string
  ) => UpdateReleaseStoreError;
  releaseId?: string;
  isBeginner?: boolean;
  clientId: string;
  subscriptionId: string;
  goToPayment: (clientId: string, id: string) => CallHistoryMethodAction;
  goToSummary: (clientId: string, id: string) => CallHistoryMethodAction;
  product?: Product;
  selectPlan: (product: Product, price: Price) => void;
}

const mapStateToProps = (state: AppState) => {
  const tracks = trackList(state.tracks.tracks, state.release.release?.id);
  const nextTrackId = tracks.length ? tracks[0].id : undefined;
  const assetStatus = nextTrackId
    ? state.assets.statuses[nextTrackId]
    : undefined;
  const trackProcessing = !assetStatus?.validationDone;
  const products = plans(state.products.list, false);
  return {
    product: products.find((p) => p.name === 'Distribution Pro'),
    isBeginner:
      state.subscription.current?.plan.toLocaleLowerCase() === 'beginner',
    clientId: state.artist.active?.id || '',
    subscriptionId: state.subscription.current?.id || '',
    nextTrackId: trackProcessing ? undefined : nextTrackId,
    stepValid: datesSection(state),
    isReleaseValid: validateRelease(state),
    missingPreviousDate:
      state.release.release?.releaseHistory === 'previous' &&
      !state.release.release.previousReleaseDate,
    missingReleaseDate: !state.release.release?.releaseDate,
    validUpc: state.release.release?.UPCEAN
      ? isUpc(state.release.release.UPCEAN)
      : true,
    missingPreOrderDate:
      state.release.release?.hasPreorder &&
      !state.release.release?.preorderDate,
    hasStores: !!state.release.release?.stores?.length,
    hasSelectedRegions: !!state.release.release?.regions?.length,
    releaseId: state.release.release?.id,
    isMobile: state.ui.mobile,
    isTablet: state.ui.tablet,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  goToPayment: (clientId: string, id: string) =>
    dispatch(
      push(
        `/payment/${clientId}/${id || ''}?redirectTo=${
          window.location.pathname
        }`
      )
    ),
  goToSummary: (clientId: string, id: string) =>
    dispatch(push(`/release/${clientId}/${id || ''}/preview`)),
  nextSection: (trackId: string) => dispatch(push(`./track/${trackId}`)),
  selectPlan: (product: Product, price: Price) =>
    dispatch(
      new CreateOrder({
        product,
        price,
        quantity: 1,
      })
    ),
  sendAnalytics: (
    field: string,
    value: string,
    error: string,
    releaseId?: string
  ) => dispatch(new UpdateReleaseStoreError(error, field, value, releaseId)),
});

export const ReleaseInfoDatePage = (props: ReleaseInfoDatePageProps) => {
  const [errorId, setErrorId] = React.useState<string | null>(null);
  const [storeErrorId, setStoreErrorId] = React.useState<string | null>(null);
  const [upcError, setUpcError] = React.useState(false);
  const [validateErrors, setValidateErrors] = React.useState(0);
  const match = useRouteMatch<{
    artistId: string;
    releaseId: string;
  }>();
  const { artistId, releaseId } = match.params;
  const handleNextStepClick = () => {
    if (isValid()) {
      props.nextSection(props.nextTrackId as string);
    } else {
      setValidateErrors(validateErrors + 1);
    }
  };

  React.useEffect(() => {
    scrollOnError();
  }, [validateErrors]);

  const isValid = () => {
    let valid = true;
    let title: string | null = null;
    let error: string | null = null;
    if (props.missingPreviousDate) {
      setErrorId('RELEASE.MISSING_PREVIOUS_DATE');
      title = 'Release Missing Previous Date';
      error = 'RELEASE.MISSING_PREVIOUS_DATE';
      valid = valid && false;
    } else if (props.missingReleaseDate) {
      setErrorId('RELEASE.MISSING_RELEASE_DATE');
      title = 'Release Missing Release Date';
      error = 'RELEASE.MISSING_RELEASE_DATE';
      valid = valid && false;
    } else if (props.missingPreOrderDate) {
      setErrorId('RELEASE.MISSING_PRE_ORDER_DATE');
      title = 'Release Missing Pre Order Date';
      error = 'RELEASE.MISSING_PRE_ORDER_DATE';
      valid = valid && false;
    } else {
      setErrorId(null);
      valid = valid && true;
    }

    if (!props.hasStores) {
      setStoreErrorId('RELEASE.MISSING_STORES');
      title = 'Release Missing Stores';
      error = 'RELEASE.MISSING_STORES';
      valid = valid && false;
    }

    if (!props.hasSelectedRegions) {
      setStoreErrorId('RELEASE.MISSING_REGIONS');
      title = 'Release Missing Regions';
      error = 'RELEASE.MISSING_REGIONS';
      valid = valid && false;
    }

    if (!props.validUpc) {
      setUpcError(true);
      title = 'Release UPC';
      error = 'INVALID_UPC';
      valid = valid && false;
    } else {
      setUpcError(false);
      valid = valid && true;
    }

    if (title && error) {
      props.sendAnalytics(title, '', error, props.releaseId);
    }

    return props.stepValid && valid && props.nextTrackId;
  };
  const handleUpgrade = () => {
    if (props.product) {
      const price = props.product?.prices.find((p) => p.frequency === 'year');

      if (!price) {
        return;
      }
      handleSelection(props.product, price);
    }
  };
  const handleSelection = (product: Product, price: Price) => {
    props.selectPlan(product, price);

    return props.goToPayment(artistId, releaseId);
  };
  return (
    <MotionOpacity className={props.className}>
      {!props.isMobile && (
        <Typography
          className="title mb-30"
          type="subheader"
          customFont="semi-bold"
        >
          <Translate id="RELEASE.SIDEBAR.LIST.DATES_STORES" />
        </Typography>
      )}

      <Wrapper>
        <Box noBorderBottom>
          <ReleaseHistoryCtrl />
          <ErrorMessage
            hasBackground
            className="mt-10"
            errorId={errorId || undefined}
          />
        </Box>

        <Box noBorderTop noBorderBottom={!props.isBeginner}>
          <BoxHeader
            title={<Translate id="RELEASE.DATES_STORES.DISTRIBUTION.TITLE" />}
          />
          <Wrapper gap={10}>
            <StoreSelectorCtrl />
            <ErrorMessage hasBackground errorId={storeErrorId || undefined} />
            <RegionSelectorCtrl />
          </Wrapper>

          <Separator />

          <Wrapper gap={10}>
            <StoreMaximizerCtrl />
            {/* <AppleMusicCtrl /> */}
            <YoutubeContentIdCtrl />
            <RemoveBrandingCtrl />
            <PreorderReleaseCtrl />
          </Wrapper>
        </Box>
        {props.isBeginner && (
          <HintArrow
            title={<Translate id="RELEASE.DATES_STORES.HINT.TITLE" />}
            noBorderRadius
            noMargin
            onClick={handleUpgrade}
          />
        )}

        <Box noBorderTop>
          <BoxHeader
            title={<Translate id="RELEASE.DATES_STORES.UPC.TITLE" />}
            text={<Translate id="RELEASE.DATES_STORES.UPC.TEXT" />}
          />
          <UPCCodeCtrl />
          <ErrorMessage
            className="mt-10"
            hasBackground
            errorId={upcError ? 'RELEASE.INVALID_UPC' : undefined}
          />
        </Box>
      </Wrapper>

      <CounterFixedButton
        id="nextReleaseDatesBtn"
        className={!props.isTablet ? '' : 'mt-30'}
        fixed={!props.isTablet}
        bottomMargin={props.isReleaseValid ? '55px' : undefined}
        isValid={true}
        currentStep={3}
        maxStep={3}
        customMaxStep={4}
        disableLoader={!!props.nextTrackId}
        isLoading={!props.nextTrackId}
        customLabel={
          !props.nextTrackId ? <Translate id="RELEASE.PROCESSING" /> : undefined
        }
        onNext={() => handleNextStepClick()}
      />
      {props.isReleaseValid && (
        <SummaryButton
          className={!props.isTablet ? '' : 'mt-30'}
          fixed={!props.isTablet}
          onClick={() => props.goToSummary(artistId, releaseId)}
        />
      )}
    </MotionOpacity>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(ReleaseInfoDatePage)`
  margin-bottom: 100px;
  @media ${({ theme }) => theme.device.laptopS} {
    margin-bottom: 0;
  }
`);

const Wrapper = styled.div<{ gap?: number }>`
  display: grid;
  grid-gap: ${({ gap }) => (gap ? `${gap}px` : '3px')};
`;

const Separator = styled.span`
  height: 1px;
  width: calc(100% + 40px);
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-bottom: 0;
  margin: 20px 0px;
  margin-left: -20px;
  display: block;

  @media ${({ theme }) => theme.device.laptopL} {
    width: calc(100% + 60px);
    margin: 30px 0px;
    margin-left: -30px;
  }
`;
